import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ExploreFilters, LabelKey } from 'services/exploreService';

const SCALAR_KEYS = ['q', 'datasource', 'manufacturer', 'model', 'type', 'year', 'view', 'has_report', 'text', 'sort', 'order'] as const;

/**
 * Explorer filters live in the URL so every filtered view is shareable and the back
 * button works. Changing any filter resets pagination.
 */
export function useUrlFilters(pageSize: number) {
  const [params, setParams] = useSearchParams();

  const filters: ExploreFilters = useMemo(() => {
    const f: ExploreFilters = {};
    SCALAR_KEYS.forEach(k => {
      const v = params.get(k);
      if (v) (f as any)[k] = v;
    });
    const labels = params.getAll('label') as LabelKey[];
    if (labels.length) f.label = labels;
    const page = Math.max(1, Number(params.get('page') || 1));
    f.offset = (page - 1) * pageSize;
    f.limit = pageSize;
    return f;
  }, [params, pageSize]);

  const page = Math.floor((filters.offset ?? 0) / pageSize) + 1;

  const update = useCallback(
    (patch: Partial<ExploreFilters> & { page?: number }) => {
      const next = new URLSearchParams(params);
      Object.entries(patch).forEach(([k, v]) => {
        if (k === 'page') return;
        next.delete(k);
        if (Array.isArray(v)) v.forEach(x => next.append(k, String(x)));
        else if (v !== undefined && v !== null && v !== '') next.set(k, String(v));
      });
      if (patch.page && patch.page > 1) next.set('page', String(patch.page));
      else next.delete('page');
      setParams(next, { replace: false });
    },
    [params, setParams]
  );

  const clear = useCallback(() => setParams(new URLSearchParams()), [setParams]);

  const activeCount = SCALAR_KEYS.filter(k => k !== 'sort' && k !== 'order' && params.get(k)).length + params.getAll('label').length;

  return { filters, page, update, clear, activeCount };
}
